import { ListItemButton } from '@mui/material';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import { apiIsoStringFormat, dayjsNow } from '@/Infrastructure/Utilities/dayjs.type';
import { LegStatus, LegType } from '@api/Trinium/types';
import { usePutLegsMutation } from '@api/Trinium/Endpoints/Leg/slice';
import { batchLegLabel, StatusActionProps } from '@components/DataGrid/Dispatch/types';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import DisabledTooltip from '@components/DisabledTooltip';
import { appendErrorAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { getApiErrorMessage } from '@api/getApiError';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { useAppDispatch } from '@reduxStore';

/**
 * Make Container Available now Action List Item
 */
export default function AvailableNowAction({ leg, onError, selectedRows }: StatusActionProps) {
  const appDispatch = useAppDispatch();
  const { number: terminalNumber } = useCurrentTerminal();
  const [putLegs] = usePutLegsMutation();

  /**
   * This action requires Leg to have LegType of FCL or ECL
   * and LegStatus of NA (Not Available) or OS (On Site)
   * */
  if (
    ![LegStatus.NA, LegStatus.OS].includes(getEnumValueByKey(LegStatus, leg.legStatus) as LegStatus) ||
    ![LegType.FCL, LegType.ECL].includes(getEnumValueByKey(LegType, leg.legType) as LegType)
  ) {
    return <></>;
  }

  function getAvailableNowStatus(leg: DispatchLeg): {
    isDisabled: boolean;
    disabledReason?: string;
  } {
    const isLegTypeFCL = getEnumValueByKey(LegType, leg.legType) === LegType.FCL;
    const legStatus = getEnumValueByKey(LegStatus, leg.legStatus);
    const transType = leg.jobCategoryTransportationType;

    if (leg.csnIsWorkflow && leg.isLive && !leg.isActiveLeg) {
      return {
        isDisabled: true,
        disabledReason: 'Only the active leg can be made available for live workflow consignments'
      };
    }

    if (!leg.isLive && !leg.isActiveLeg && transType === 'Import') {
      return {
        isDisabled: true,
        disabledReason: 'Only the active leg can be made available for import consignments'
      };
    }

    if (transType === 'Export' && isLegTypeFCL && !leg.isActiveLeg && legStatus !== LegStatus.OS) {
      return {
        isDisabled: true,
        disabledReason: 'Only legs with On Site status can be made available for FCL export consignments'
      };
    }

    return { isDisabled: false };
  }

  const candidateRows = selectedRows?.some((selectedRow) => selectedRow.legId === leg.legId) ? selectedRows : [leg];
  const targetRows = candidateRows.filter(
    (row) =>
      [LegStatus.NA, LegStatus.OS].includes(getEnumValueByKey(LegStatus, row.legStatus) as LegStatus) &&
      [LegType.FCL, LegType.ECL].includes(getEnumValueByKey(LegType, row.legType) as LegType) &&
      !getAvailableNowStatus(row).isDisabled
  );

  const handleBtnClick = () => {
    void putLegs({
      terminalNumber,
      actionType: {
        action: 'setAvailability',
        data: {
          legIds: targetRows.map((targetRow) => targetRow.legId),
          availDate: dayjsNow().format(apiIsoStringFormat)
        }
      }
    })
      .unwrap()
      .then((response) => {
        response.errors.forEach((error) => {
          if (onError) onError(error.errorMessage);
          else appDispatch(appendErrorAnnouncement({ message: error.errorMessage }));
        });
      })
      .catch((error: unknown) => {
        const errorMessage = getApiErrorMessage(error);
        if (onError) onError(errorMessage);
        else appDispatch(appendErrorAnnouncement({ message: errorMessage }));
      });
  };

  /** The clicked leg gates the action so the row the dispatcher is looking at is never silently skipped */
  const { isDisabled, disabledReason } = getAvailableNowStatus(leg);

  return (
    <DisabledTooltip message={isDisabled ? disabledReason : undefined}>
      <ListItemButton
        disabled={isDisabled}
        onClick={handleBtnClick}>
        {batchLegLabel('Available Now', targetRows.length)}
      </ListItemButton>
    </DisabledTooltip>
  );
}
